// team-repos.js — Team → Repos panel (PR F of the
// repo→Workspace routing arc, 2026-05-15).
//
// Lists the tenant's repo allow-list (workspace_repos) with a
// per-row Workspace dropdown that the admin can change to
// assign the repo to a specific Workspace. Backed by:
//   GET   /v1/teams/repos                    — list (read:settings)
//   PATCH /v1/teams/repos/{owner/repo}/bu    — assign (admin:members)
//
// PR E already wired the coordinator to ACCEPT daemon-supplied
// bu_id on push. This page is how admins TELL the daemon which
// Workspace each repo belongs to. Daemons read the assignments
// (future PR) and tag pushes accordingly.
//
// Read-only fallback: non-admins see the table but the dropdown
// is disabled. The server enforces the same gate, so this is
// a UI affordance, not a security boundary.

import { $ } from "./dom.js";
import { escapeHTML, ago } from "./util.js";
import { apiWithMessage } from "./api.js";
import { showToast } from "./ui.js";
import { teamState } from "./page-state.js";

// 5s cache matching team-bus.js's pattern. Gated on
// (tenantId, canManage) so a role-flip mid-session doesn't keep
// showing stale "edit" affordances.
let reposCache = null;
let busCache = null; // workspaces list — needed for the dropdown options
let cacheTenant = null;
let cacheCanManage = null;
let lastFetch = 0;
const FETCH_TTL_MS = 5000;

export async function renderReposPanel(canManage) {
  const panel = $("team-repos-panel");
  const listEl = $("team-repos-list");
  if (!panel || !listEl) return;

  const tenantId = (teamState.info && teamState.info.team_id) || "";
  const cacheValid =
    reposCache &&
    busCache &&
    cacheTenant === tenantId &&
    cacheCanManage === canManage &&
    Date.now() - lastFetch < FETCH_TTL_MS;
  if (!cacheValid) {
    try {
      // Parallel fetch — repos for the table, bus for the dropdown options.
      const [reposResp, busResp] = await Promise.all([
        apiWithMessage("/v1/teams/repos"),
        apiWithMessage("/v1/teams/bus"),
      ]);
      reposCache = Array.isArray(reposResp?.repos) ? reposResp.repos : [];
      busCache = Array.isArray(busResp?.bus) ? busResp.bus : [];
      cacheTenant = tenantId;
      cacheCanManage = canManage;
      lastFetch = Date.now();
    } catch (err) {
      const msg = (err && err.message) || "";
      // 403 / 404 / unauthorized are the expected unhappy paths
      // (non-member, migration not run, etc.). Hide the panel
      // silently for those. Anything else gets a toast.
      if (!/forbidden|not found|unauthorized/i.test(msg)) {
        console.warn("[team-repos] fetch failed:", err);
        showToast("error", "Couldn't load Repos: " + msg);
      }
      panel.style.display = "none";
      return;
    }
  }

  // Hide the panel if the tenant has no repos in its allow-list.
  // (This is the typical case for tenants that haven't completed
  // the GitHub App install yet.) Showing an empty panel here would
  // be confusing — it suggests a feature that hasn't been
  // configured rather than one that's empty.
  if (!reposCache || reposCache.length === 0) {
    panel.style.display = "none";
    return;
  }
  panel.style.display = "";

  // Build the dropdown options once — same options across every
  // row. The default is "Any Workspace" (bu_id=null), then the
  // tenant's BUs in default-first / alpha order.
  const sortedBus = [...busCache].sort((a, b) => {
    if (a.is_default && !b.is_default) return -1;
    if (!a.is_default && b.is_default) return 1;
    return (a.name || "").localeCompare(b.name || "");
  });

  listEl.innerHTML = reposCache
    .map((r) => renderRepoRow(r, sortedBus, canManage))
    .join("");

  if (canManage) wireRepoRowHandlers(listEl);
}

function renderRepoRow(repo, sortedBus, canManage) {
  const currentBuId = repo.bu_id || "";
  const options = [
    `<option value="" ${currentBuId === "" ? "selected" : ""}>Any workspace (default)</option>`,
    ...sortedBus.map((b) => {
      const selected = currentBuId === b.bu_id ? "selected" : "";
      const suffix = b.is_default ? " (default)" : "";
      return `<option value="${escapeHTML(b.bu_id)}" ${selected}>${escapeHTML(b.name)}${suffix}</option>`;
    }),
  ].join("");

  return `
    <div class="repo-row" data-repo="${escapeHTML(repo.repo_full_name)}" style="display: flex; align-items: center; justify-content: space-between; padding: var(--s3) var(--s5); border-bottom: 1px solid var(--border-subtle, var(--border)); gap: var(--s3);">
      <div style="display: flex; align-items: center; gap: var(--s2); min-width: 0; flex: 1;">
        <span style="font-family: var(--font-mono); font-size: 13px; color: var(--text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${escapeHTML(repo.repo_full_name)}</span>
        <span style="font-size: 11px; color: var(--text-tertiary); white-space: nowrap;">added ${escapeHTML(ago(repo.added_at))}</span>
      </div>
      <select class="repo-row-bu-select" data-repo="${escapeHTML(repo.repo_full_name)}"
        ${canManage ? "" : "disabled"}
        style="min-width: 180px; padding: 6px 8px; background: var(--bg-elevated); border: 1px solid var(--border); border-radius: var(--r-sm); color: var(--text); font-size: 12px;">
        ${options}
      </select>
    </div>`;
}

function wireRepoRowHandlers(listEl) {
  listEl.querySelectorAll(".repo-row-bu-select").forEach((sel) => {
    // PR F sweep finding #5 (MED): initialize previousValue at wire
    // time so the first-interaction revert path has the actual
    // prior bu_id. Without this, a row that started at "bu_eng"
    // and changes to "" → PATCH fails → reverts to "" (the
    // dataset default) instead of "bu_eng". The user would see
    // the row land on "Any workspace" silently after a failed
    // assignment, with no signal that the prior state was lost.
    sel.dataset.previousValue = sel.value;
    sel.addEventListener("change", async (ev) => {
      const target = ev.currentTarget;
      const repo = target.getAttribute("data-repo") || "";
      const newBuId = target.value || null;
      // Optimistic: leave the select on its new value; if the
      // PATCH fails, revert. This matches the pattern other
      // dashboard surfaces use (snappier UX, fail-loud on error).
      const previousValue = target.dataset.previousValue || "";
      target.dataset.previousValue = target.value;
      target.disabled = true;
      try {
        await apiWithMessage(`/v1/teams/repos/${encodeURIComponent(repo)}/bu`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ bu_id: newBuId }),
        });
        // Update cache so a re-render in <5s doesn't flicker the
        // old value back.
        if (reposCache) {
          const row = reposCache.find((r) => r.repo_full_name === repo);
          if (row) row.bu_id = newBuId;
        }
        const label = newBuId ? "Workspace" : "(any)";
        showToast("success", `${repo} → ${label === "(any)" ? "Any workspace" : "assigned"}`);
      } catch (err) {
        // Revert the dropdown to the prior value.
        target.value = previousValue;
        const msg = (err && err.message) || "Failed to assign repo";
        showToast("error", msg);
      } finally {
        target.disabled = false;
      }
    });
  });
}

// Invalidate the cache from outside (e.g., after BU CRUD that
// might have added/removed Workspaces). Called from team-bus.js
// so a newly-created Workspace shows up in this panel's
// dropdowns without waiting the full 5s TTL.
export function invalidateReposCache() {
  reposCache = null;
  busCache = null;
}
